import { useState, useMemo } from "react"
import { useOutletContext } from "react-router-dom"
import { ShieldCheck } from "lucide-react"
import { differenceInDays } from "date-fns"
import ToolShell from "../components/ToolShell"
import FormulaBox from "../components/FormulaBox"
import ToolFAQ from "../components/ToolFAQ"
import CTABox from "../components/CTABox"
import RelatedToolsList from "../components/RelatedToolsList"
import { ToolDef } from "../toolRegistry"

const STATE_RATES: Record<string, { rate: number; required: boolean; note: string }> = {
  NY: { rate: 1.0, required: true, note: "Required for buildings with 6+ units" },
  NJ: { rate: 2.5, required: true, note: "Rate set annually by state" },
  CT: { rate: 1.5, required: true, note: "Required for all residential tenancies" },
  MA: { rate: 1.0, required: true, note: "Payable annually or on termination" },
  MD: { rate: 1.5, required: true, note: "Required for deposits held >6 months" },
}

const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA",
  "KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ",
  "NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT",
  "VA","WA","WV","WI","WY",
]

export default function SecurityDepositInterestCalculatorPage() {
  const { tool, currency } = useOutletContext<{ tool: ToolDef, currency: string }>()

  const [depositAmount, setDepositAmount] = useState("")
  const [moveInDate, setMoveInDate] = useState("")
  const [moveOutDate, setMoveOutDate] = useState("")
  const [selectedState, setSelectedState] = useState("")
  const [manualRate, setManualRate] = useState("")

  const stateInfo = selectedState ? STATE_RATES[selectedState] || { required: false, note: "Your state does not require interest on security deposits", rate: 0 } : null

  const effectiveRate = useMemo(() => {
    if (manualRate) return parseFloat(manualRate)
    if (stateInfo) return stateInfo.rate
    return 0
  }, [manualRate, stateInfo])

  const results = useMemo(() => {
    const deposit = parseFloat(depositAmount)
    if (!deposit || !moveInDate || !moveOutDate || !effectiveRate) return null

    const moveIn = new Date(moveInDate)
    const moveOut = new Date(moveOutDate)
    const daysHeld = differenceInDays(moveOut, moveIn)
    if (daysHeld <= 0) return null

    const yearsHeld = daysHeld / 365
    const interest = deposit * (effectiveRate / 100) * yearsHeld
    const totalReturn = deposit + interest

    return { daysHeld, yearsHeld, interest, totalReturn }
  }, [depositAmount, moveInDate, moveOutDate, effectiveRate])

  const fmt = (n: number) => `$${n.toFixed(2)}`

  return (
    <div className="space-y-8">
      <ToolShell>
        <div className="p-6">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Input Panel */}
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5 space-y-4">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Deposit Details</p>

              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Security Deposit Amount</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span>
                  <input
                    type="number"
                    min="0"
                    value={depositAmount}
                    onChange={e => setDepositAmount(e.target.value)}
                    placeholder="e.g. 2400"
                    className="w-full rounded-lg border border-slate-200 bg-white pl-7 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">Move-in Date</label>
                  <input
                    type="date"
                    value={moveInDate}
                    onChange={e => setMoveInDate(e.target.value)}
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">Move-out Date</label>
                  <input
                    type="date"
                    value={moveOutDate}
                    onChange={e => setMoveOutDate(e.target.value)}
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">US State</label>
                <select
                  value={selectedState}
                  onChange={e => { setSelectedState(e.target.value); setManualRate("") }}
                  className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                >
                  <option value="">Select state...</option>
                  {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>

              {stateInfo && (
                <div className={`rounded-xl border px-4 py-3 text-xs ${stateInfo.required ? "bg-blue-50 border-blue-200 text-blue-700" : "bg-slate-50 border-slate-200 text-slate-600"}`}>
                  {stateInfo.required
                    ? <><strong>Interest required.</strong> Rate: {stateInfo.rate}%/yr — {stateInfo.note}</>
                    : stateInfo.note
                  }
                </div>
              )}

              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">Manual Interest Rate % (overrides state)</label>
                <div className="relative">
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">%</span>
                  <input
                    type="number"
                    min="0"
                    step="0.1"
                    value={manualRate}
                    onChange={e => setManualRate(e.target.value)}
                    placeholder={stateInfo ? `Default: ${stateInfo.rate}%` : "e.g. 2.5"}
                    className="w-full rounded-lg border border-slate-200 bg-white pl-3 pr-8 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                  />
                </div>
              </div>
            </div>

            {/* Result Panel */}
            <div className="rounded-2xl border border-primary/20 bg-primary/5 p-5 space-y-4">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Results</p>

              {!results ? (
                <p className="text-slate-400 text-sm text-center py-10">Enter deposit details and dates to calculate interest</p>
              ) : (
                <>
                  <div className="text-center py-3">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Total Deposit to Return</p>
                    <p className="text-5xl font-extrabold text-primary">{fmt(results.totalReturn)}</p>
                  </div>

                  <div className="border-t border-primary/10 pt-4 space-y-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Original Deposit</span>
                      <span className="font-semibold text-slate-900">{fmt(parseFloat(depositAmount))}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Interest Owed</span>
                      <span className="font-semibold text-green-600">{fmt(results.interest)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Period Held</span>
                      <span className="font-semibold text-slate-900">{results.daysHeld} days ({results.yearsHeld.toFixed(2)} yrs)</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Rate Applied</span>
                      <span className="font-semibold text-slate-900">{effectiveRate}% / year</span>
                    </div>
                  </div>

                  {results.daysHeld > 365 && (
                    <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-xs text-amber-700">
                      Deposit held &gt;12 months — confirm return was issued on time per your state's rules.
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </ToolShell>

      <FormulaBox
        formula={`Days Held = Move-out Date − Move-in Date\nYears Held = Days Held ÷ 365\n\nInterest = Deposit × (Rate ÷ 100) × Years Held  [Simple Interest]\nTotal Return = Deposit + Interest`}
        description="This calculator uses simple interest — the same method most states that require deposit interest specify. Compound interest is not typically mandated."
      />

      <ToolFAQ
        items={[
          {
            q: "Which US states require security deposit interest?",
            a: "States that require landlords to pay interest on security deposits include: New York, New Jersey, Connecticut, Massachusetts, Maryland, Illinois, Iowa, and a few others. Rules vary — some require interest only above certain deposit amounts or holding periods. Always verify current state law.",
          },
          {
            q: "How long do I have to return a security deposit?",
            a: "Return deadlines vary by state: California (21 days), New York (14 days), Texas (30 days), Florida (15–60 days). Failure to return within the deadline can result in penalties of up to 2–3× the deposit amount in some states.",
          },
          {
            q: "Can I deduct from the security deposit before returning it?",
            a: "Yes — but only for legitimate deductions: unpaid rent, damage beyond normal wear and tear, or cleaning costs if the tenant left the unit in poor condition. You must typically provide an itemized list of deductions with receipts within the return deadline.",
          },
        ]}
      />

      <CTABox
        icon={ShieldCheck}
        heading="Tracking deposits per lease?"
        body={tool.ctaText}
        ctaLabel="Start free"
        ctaHref="/register"
      />

      <RelatedToolsList relatedSlugs={tool.related} />
    </div>
  )
}
